import React from 'react';
import { useRecoilState } from 'recoil';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import get from 'lodash/get';
import { styled } from 'linaria/react';
import { Adaptive, RegularButton as Button, Typography } from 'fronton-react';
import { QuestionIcon } from '@fronton/icons-react';
import { capitalize } from 'utils/capitalize';
import { doorsWindowsArea } from 'pages/dimensions/store/area';
import { gaFocusOnDimensions } from 'ga-events/ga-focus-dimensions';
import { gaClickOnGoods } from 'ga-events/ga-click-goods';
import { dimensionsLabels } from '../../const';
import { GoodsFields } from '../../types';
import { GoodsDimensionsForm } from './goods-dimensions.form';
import { Hint } from '../../components/Hint';
import { appendFields } from '../helpers';
import { InputContainerColumn, InputContainerRow } from '../styles';
import { useFilteredInputParams } from '../hooks/use-filtered-input-params';
import { doorsWindowsInputs, useFormConditions } from '../../hooks/use-dimension-form-conditions';
import { goodsErrors, goodsHeadings, SHOULD_BE_MORE_THEN_ZERO } from '../const';
import { AdaptiveInput } from '../../components/AdaptiveInput';

export const DoorsWindowsForm = () => {
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext();
  const doorsFieldsProps = useFieldArray({ name: 'doorsFields' });
  const windowFieldProps = useFieldArray({ name: 'windowsFields' });
  const [isAreaEnabled, setIsAreaEnabled] = useRecoilState(doorsWindowsArea);

  const { inputParameters } = useFilteredInputParams();
  const { hasDoors, hasWindows } = useFormConditions();

  const doorsCount = watch().doors || 0;
  const windowsCount = watch().windows || 0;

  const goodsFields: {
    [key: string]: {
      count: number;
      append: (value: { width: string; height: string }) => void;
      remove: (index?: number | number[]) => void;
      fieldsLength: number;
      isDecreased: boolean;
      isIncreased: boolean;
    };
  } = {
    [GoodsFields.doors]: {
      count: Number(doorsCount),
      append: doorsFieldsProps.append,
      remove: doorsFieldsProps.remove,
      fieldsLength: doorsFieldsProps.fields.length,
      isDecreased: +doorsCount < doorsFieldsProps.fields.length,
      isIncreased: +doorsCount > doorsFieldsProps.fields.length,
    },
    [GoodsFields.windows]: {
      count: Number(windowsCount),
      append: windowFieldProps.append,
      remove: windowFieldProps.remove,
      fieldsLength: windowFieldProps.fields.length,
      isDecreased: +windowsCount < windowFieldProps.fields.length,
      isIncreased: +windowsCount > windowFieldProps.fields.length,
    },
  };

  const onAreaButtonClick = () => {
    setIsAreaEnabled(!isAreaEnabled);

    gaClickOnGoods({ title, name: isAreaEnabled ? 'Указать количество' : 'Указать размеры' });

    Object.keys(goodsFields).forEach(fieldName => {
      if (!isAreaEnabled) {
        if (goodsFields[fieldName].isDecreased) {
          goodsFields[fieldName].remove();
          appendFields({
            fields: Number(get(goodsFields, `${fieldName}.count`)),
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            append: get(goodsFields, `${fieldName}.append`),
            value: { width: '', height: '' },
          });
        } else if (goodsFields[fieldName].isIncreased) {
          const count =
            Number(get(goodsFields, `${fieldName}.count`)) -
            Number(get(goodsFields, `${fieldName}.fieldsLength`));
          appendFields({
            fields: count,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            append: get(goodsFields, `${fieldName}.append`),
            value: { width: '', height: '' },
          });
        }
      }
    });
  };

  const filteredInputs = doorsWindowsInputs.filter(goods => inputParameters.includes(goods));
  const defaultFilteredInput = get(goodsHeadings, `${filteredInputs[0]}`, '');

  const title = filteredInputs
    .slice(1)
    .reduce(
      (acc, fieldName) => `${acc} и ${goodsHeadings[fieldName]}`,
      `${capitalize(defaultFilteredInput)}`
    );

  const onInputFocus = (fieldName: string) => () => {
    gaFocusOnDimensions({ fieldName: dimensionsLabels[fieldName], title });
  };

  return (
    <>
      <ButtonContainer>
        <HeaderContainer>
          <Typography variant="l" color="text-primary" size="body_accent">
            {title}
          </Typography>
          <Hint
            hintText="Площадь рассчитывается по среднему значению: Дверь = ~1,4 м², Окно = ~2,73 м²"
            title={title}>
            <QuestionIcon type="outline" size="l" color="text-primary" />
          </Hint>
        </HeaderContainer>

        <Button
          variant="outline"
          label={isAreaEnabled ? 'Указать количество' : 'Указать размеры'}
          size="s"
          onClick={onAreaButtonClick}>
          {isAreaEnabled ? 'Указать количество' : 'Указать размеры'}
        </Button>
      </ButtonContainer>
      {!isAreaEnabled ? (
        <InputsContainer>
          <InputContainerRow>
            {filteredInputs.map(parameter => (
              <InputContainerColumn key={parameter}>
                <Controller
                  name={parameter}
                  control={control}
                  rules={{
                    validate: {
                      required: value => {
                        if (value === '0') return SHOULD_BE_MORE_THEN_ZERO;
                        return undefined;
                      },
                    },
                    pattern: {
                      value: /^[0-9]{1,2}$/,
                      message: goodsErrors.count.pattern,
                    },
                  }}
                  render={({ field: { value, onChange } }) => (
                    <>
                      <Adaptive to="tablet">
                        <AdaptiveInput
                          form={{ value, onChange, onFocus: onInputFocus(parameter), errors }}
                          name={parameter}
                          locale={{ labels: dimensionsLabels }}
                          mobile
                        />
                      </Adaptive>
                      <Adaptive from="tablet">
                        <AdaptiveInput
                          form={{ value, onChange, onFocus: onInputFocus(parameter), errors }}
                          name={parameter}
                          locale={{ labels: dimensionsLabels }}
                          mobile={false}
                        />
                      </Adaptive>
                    </>
                  )}
                />
              </InputContainerColumn>
            ))}
          </InputContainerRow>
        </InputsContainer>
      ) : (
        <>
          {hasDoors && (
            <GoodsDimensionsForm
              name="doorsFields"
              type="doors"
              formProps={doorsFieldsProps}
              title={title}
            />
          )}
          {hasWindows && (
            <GoodsDimensionsForm
              name="windowsFields"
              type="windows"
              formProps={windowFieldProps}
              title={title}
            />
          )}
        </>
      )}
    </>
  );
};

const ButtonContainer = styled.div`
  display: flex;
  @media (min-width: 768px) {
    align-items: center;
    justify-content: space-between;
  }
  @media (max-width: 767px) {
    flex-direction: column;
    align-items: start;
    gap: 8px;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InputsContainer = styled.div`
  margin-top: 24px;
`;
